import { Button, Typography, Stack, Popconfirm, Badge } from "@altimateai/lego";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { DataTable } from "@/components/tables";
import type { TableHeaderType } from "@/components/tables/types";
import type { SSOGroupMappingResponse } from "./types";
import { MATCH_TYPES } from "./types";

type MappingRow = SSOGroupMappingResponse & { id: number };

interface MappingTableProps {
  mappings: SSOGroupMappingResponse[];
  mappingType: "team" | "role";
  isLoading: boolean;
  onEdit: (mapping: SSOGroupMappingResponse) => void;
  onDelete: (mappingId: number) => void;
  isDeleting: boolean;
}

const getMatchTypeLabel = (value: string) =>
  MATCH_TYPES.find(t => t.value === value)?.label ?? value;

export const MappingTable = ({
  mappings,
  mappingType,
  isLoading,
  onEdit,
  onDelete,
  isDeleting,
}: MappingTableProps) => {
  if (!isLoading && !mappings.length) {
    return (
      <Stack className="al-p-8 al-items-center">
        <Typography variant="body" className="al-text-gray-500">
          No {mappingType} mappings configured yet. Click &quot;Add Mapping&quot; to create one.
        </Typography>
      </Stack>
    );
  }

  const header: TableHeaderType<MappingRow>[] = [
    {
      id: 1,
      label: "Group Pattern",
      uid: "external_group_identifier",
      width: "22%",
      render: item => (
        <span className="al-font-mono al-text-sm">{item.external_group_identifier}</span>
      ),
    },
    {
      id: 2,
      label: "Match Type",
      uid: "match_type",
      width: "12%",
      render: item => <Badge variant="secondary">{getMatchTypeLabel(item.match_type)}</Badge>,
    },
    {
      id: 3,
      label: "Target",
      uid: "target_name",
      width: "18%",
      render: item => <span>{item.target_name ?? `ID: ${item.target_id}`}</span>,
    },
    {
      id: 4,
      label: "Status",
      uid: "is_active",
      width: "10%",
      render: item => (
        <Badge variant={item.is_active ? "success" : "outline"}>
          {item.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: 5,
      label: "Created By",
      uid: "created_by_email",
      width: "18%",
      render: item => (
        <span className="al-text-gray-500 al-text-xs">{item.created_by_email ?? "\u2014"}</span>
      ),
    },
    {
      id: 6,
      label: "Actions",
      uid: "actions",
      width: "12%",
      render: item => (
        <Stack direction="row" className="al-justify-end al-gap-1">
          <Button variant="ghost" size="sm" onClick={() => onEdit(item)} aria-label="Edit mapping">
            <IconPencil size={16} />
          </Button>
          <Popconfirm
            title="Delete mapping"
            description="Are you sure? This action cannot be undone."
            onConfirm={() => onDelete(item.id)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button variant="ghost" size="sm" disabled={isDeleting} aria-label="Delete mapping">
              <IconTrash size={16} className="al-text-red-500" />
            </Button>
          </Popconfirm>
        </Stack>
      ),
    },
  ];

  return (
    <DataTable<MappingRow>
      header={header}
      data={{ items: mappings as MappingRow[] }}
      idKey={item => String(item.id)}
      isLoading={isLoading}
    />
  );
};
